/**
 * DI-3-F — a set's upload, as the runner holds it and the session record
 * persists it (`ClusterIntake.upload`, IndexedDB — the DB version is NOT
 * bumped: only the value shape of a `sessions` row grows, no store changes).
 *
 *   queued → uploading {bytesSent, bytesTotal, filesDone} → committing
 *          → ingesting {processed, total} → present | failed {failedCount}
 *
 * `queued` covers "waiting for its turn" AND "waiting for the start rule"
 * (N2): "now" starts once the set's round trip is `synced`; "after" once the
 * pilot's Approve answered `approved_pending_upload` (`approvedAt`). A pass
 * is one upload session's worth: it sends what the ledger lacks and completes
 * a manifest of every file the server has not verified yet (`verified`);
 * pass 2 is the automatic retry, pass 3+ the pilot's "Retry the upload".
 * `error` is a transport stop (the uplink or the gateway down — edge ak): the
 * phase stays, the runner tries again later.
 *
 * A retry pass completes exactly the files not verified yet
 * (`manifest.ts:passManifest`): after a FAILED run the gateway starts a new
 * attempt for the identical manifest (inspection-3/v1 2.3.2 — DI-3-P3). A
 * record written before DI-3-P3 may still carry a `completed` list; nothing
 * reads it.
 */
import type { Ledger } from "./ledger.js";
import type { ImageUploadMode, UploadChoice } from "../session/sessionStore.js";
import {
  formatBytes,
  formatShort,
  remainingS,
  uploadRate,
} from "../board/eta.js";

export const UPLOAD_PHASES = [
  "queued",
  "uploading",
  "committing",
  "ingesting",
  "present",
  "failed",
] as const;
export type UploadPhase = (typeof UPLOAD_PHASES)[number];

export interface SetUpload {
  phase: UploadPhase;
  /** "after verification": when Approve answered `approved_pending_upload`. */
  approvedAt: string | null;
  /** 1 = every file; 2 = the automatic retry pass; 3+ = "Retry the upload". */
  pass: number;
  /** Files a finished run verified present (the next manifest leaves them out). */
  verified: string[];
  /** The upload session of this pass (where the manifest is completed). */
  sessionId: string | null;
  /** Set once the manifest was accepted (`completeUploadSession`). */
  jobId: string | null;
  ledger: Ledger;
  bytesTotal: number;
  bytesSent: number;
  filesTotal: number;
  filesDone: number;
  /** The ingest poll: files verified / files of the manifest. */
  processed: number;
  total: number;
  /** When the first block of THIS set went out (the tile's rate clock). */
  startedAt: string | null;
  /** Bytes sent since `startedAt` in THIS tab (the rate's numerator). */
  sentSinceStart?: number;
  /** Files the ingest refused on the last pass ("failed · 3 files"). */
  refused: string[];
  failedCount: number;
  /** The poll's `dataAcquisitionState` at the end ("PROCESSING" = promoted). */
  dataAcquisitionState: string | null;
  /** A transport stop (edge ak): the words, and when the runner tries again. */
  error: string | null;
  retryAt: string | null;
  finishedAt: string | null;
  /**
   * DI-3-P7 — when the pilot pressed "Upload now" on an "after
   * verification" set (the choice became "now"); absent otherwise.
   */
  nowAt?: string | null;
  /**
   * DI-3-S9 — the resume's reconcile with the server, for the pass it ran
   * before (`pass`); absent on a fresh upload. Not persisted while
   * `checking` (a reload checks again).
   */
  reconcile?: UploadReconcile | null;
  /**
   * DI-3-S9 — originals the last FAILED ingest reported `blob_absent`
   * (distinct names); null / absent once a pass completed or failed otherwise.
   */
  missing?: number | null;
  /**
   * DI-3-S68 T1 — the server refused the set's upload for good (409
   * `da_state_not_uploadable`, 404 on the open): it is never started again —
   * not by the driver, not by the upload lane. Absent otherwise.
   */
  ended?: "da_state_not_uploadable" | "set_gone" | null;
}

/** DI-3-S9 — what the resume's reconcile found (`runner/uploadDriver.ts`). */
export type UploadReconcile =
  | { state: "checking"; pass: number }
  | {
      state: "done";
      pass: number;
      /** Ledger entries before the reconcile ("370 marked sent here"). */
      marked: number;
      /** Entries kept (the server did not mark them missing). */
      kept: number;
      /** Names the server knows it lacks (`listIntakeBlobs?state=missing`, "3 known missing on the server"). */
      knownMissing: number;
      /** Files of the pass still to send after it ("sending 160"). */
      sending: number;
    }
  /** The read failed: the pass sends what the ledger lacks, as before S9. */
  | { state: "failed"; pass: number };

/** A partner set's withhold (story §2.2): once, at `verdict_ready` / `needs_info`. */
export interface SetWithhold {
  state: "waiting" | "done" | "refused";
  at: string | null;
  imagesWithheld: number | null;
  reason: string | null;
}

/** Automatic passes before the tile reads "failed" (story §5: one retry pass). */
export const MAX_AUTO_PASSES = 2;

export function initialUpload(): SetUpload {
  return {
    phase: "queued",
    approvedAt: null,
    pass: 1,
    verified: [],
    sessionId: null,
    jobId: null,
    ledger: {},
    bytesTotal: 0,
    bytesSent: 0,
    filesTotal: 0,
    filesDone: 0,
    processed: 0,
    total: 0,
    startedAt: null,
    refused: [],
    failedCount: 0,
    dataAcquisitionState: null,
    error: null,
    retryAt: null,
    finishedAt: null,
  };
}

/** The upload is over for good (present, or failed until the pilot retries). */
export function uploadFinished(u: SetUpload | null | undefined): boolean {
  return u?.phase === "present" || u?.phase === "failed";
}

/** The set was promoted by the ingest (approved + every blob verified). */
export function uploadPromoted(u: SetUpload | null | undefined): boolean {
  return u?.phase === "present" && u.dataAcquisitionState === "PROCESSING";
}

/**
 * Does this pass still need the FILES (the folder read in this tab)? Only
 * while bytes are to be sent or the manifest is to be built — a completed
 * manifest's poll needs no file.
 */
export function uploadNeedsFiles(u: SetUpload | null | undefined): boolean {
  if (!u || uploadFinished(u)) return false;
  return !(u.jobId && u.sessionId);
}

// ── DI-3-P7 — "have my images been uploaded?" in one sentence ──────────────

/** The D91 chip of the Upload column (Intake History, My Intake). */
export type UploadChipKind =
  | "not_required"
  | "queued"
  | "uploading"
  /** DI-3-S4-FE — this browser's record: started, interrupted, not finished. */
  | "unfinished"
  | "done"
  | "failed";

/** What the sentence knows about a set from THIS browser (the runner or its record). */
export interface UploadSubject {
  /** `null` = the workflow withholds the images (the runner's rule, N2). */
  uploadChoice: UploadChoice | null;
  upload?: SetUpload | null | undefined;
  /** M5-NODE-01 — the version's mode at Start (this browser's record). */
  imageUpload?: ImageUploadMode | undefined;
}

/** The list row's `upload` (intake/v1 1.13.0, `IntakeUploadState`). */
export interface ServerUploadCounts {
  required: boolean;
  present: number;
  total: number;
}

/**
 * DI-3-S10-FE (intake/v1 1.21.0) — who withheld a partner set's images, as
 * the wire says it (`IntakeStatus.withheldBy` / `IntakeListItem.withheldBy`):
 * the pilot's withhold (`pilot`, "in the field") or Approve's inline one
 * (`approval`); `null` = not withheld, or withheld before 1.21.0.
 */
export type WithheldBy = "pilot" | "approval";

/** The status fields the sentence reads (the Upload stage's `progress` = present / count). */
export interface UploadStatusLike {
  images: { count: number };
  /** M5-NODE-01 — the served mode (intake/v1 1.37.0). */
  imageUpload?: ImageUploadMode | undefined;
  /** DI-3-S10-FE — the wire's `withheldBy` (absent on a gateway before 1.21.0). */
  withheldBy?: WithheldBy | null | undefined;
  stages?:
    | readonly {
        key: string;
        state: string;
        progress?: { done: number; total: number } | null;
      }[]
    | undefined;
}

export interface UploadSummaryContext {
  /**
   * The runner of THIS tab holds the set: its actions ("Retry the upload",
   * "Upload now") run here. False: the actions read "in the tab that runs
   * this drop".
   */
  here?: boolean | undefined;
  /**
   * The runner phase is live (this tab's runner, or the active session's
   * record that its owner tab writes on every phase change). False (a table
   * row of an earlier session): an in-flight phase may be stale — only the
   * server's count is said for it.
   */
  live?: boolean | undefined;
  /** "queued · after set 2" — the board's queue position (`uploadQueueLabels`). */
  queueLabel?: string | null | undefined;
  /** The clock of the rate / time left (ms). */
  nowMs?: number | undefined;
  /**
   * DI-3-S7-FE — the images an approval in this tab withheld inline
   * (`withheldByApprovalOf`); a number turns the "not required" sentence
   * into "… — withheld by the approval".
   */
  withheldByApproval?: number | null | undefined;
  /**
   * DI-3-S10-FE — the list row's `withheldBy` (a server card, an Intake
   * History row); the status' wins once a status is read.
   */
  withheldBy?: WithheldBy | null | undefined;
  /** M5-NODE-01 — the list row's `imageUpload` (the status' / the set's win). */
  imageUpload?: ImageUploadMode | undefined;
}

export interface UploadSummary {
  /** The one sentence ("527 of 527 uploaded"); "" when nothing is known. */
  text: string;
  /** The Upload column's chip; null = nothing known (the cell reads "—"). */
  kind: UploadChipKind | null;
  /** The action the sentence offers. */
  action: "retry" | "upload_now" | null;
  /** The action exists but runs only in the tab that runs this drop. */
  elsewhere: boolean;
  /**
   * DI-3-S9 — why the pass sends what it sends (`passCause`), when the
   * sentence carries one: the card's Upload fact shows it under the numbers.
   */
  cause?: string | null;
}

export const UPLOAD_NOT_REQUIRED =
  "image upload not required for this workflow";
/**
 * DI-3-S7-FE (intake/v1 1.19.0) — the sentence after an approval in this tab
 * withheld the partner set's images inline (`ApproveResult.imagesWithheld`).
 * The chip's kind stays `not_required`.
 */
export const UPLOAD_WITHHELD_BY_APPROVAL = `${UPLOAD_NOT_REQUIRED} — withheld by the approval`;
/** DI-3-S10-FE (intake/v1 1.21.0) — the pilot withheld the set (`withheldBy: pilot`). */
export const UPLOAD_WITHHELD_IN_FIELD = `${UPLOAD_NOT_REQUIRED} — withheld in the field`;

/**
 * DI-3-S10-FE — the "not required" sentence, from the wire first: `approval`
 * → "… — withheld by the approval", `pilot` → "… — withheld in the field".
 * When the wire names nobody, this tab's approval (`bridge`, S7-FE's count —
 * `withheldByApprovalOf`, which drops it once a status read is newer than
 * the click: S1's wire-wins rule) still says "by the approval"; else the
 * plain sentence.
 */
export function withheldSentence(
  wire: WithheldBy | null | undefined,
  bridge: number | null | undefined,
): string {
  if (wire === "approval") return UPLOAD_WITHHELD_BY_APPROVAL;
  if (wire === "pilot") return UPLOAD_WITHHELD_IN_FIELD;
  return typeof bridge === "number"
    ? UPLOAD_WITHHELD_BY_APPROVAL
    : UPLOAD_NOT_REQUIRED;
}
export const UPLOAD_AFTER_VERIFICATION =
  "upload after verification — approve to start";
export const UPLOAD_FROM_ANOTHER_BROWSER = "an upload from another browser";
export const UPLOAD_ELSEWHERE_HINT = "in the tab that runs this drop";

// ── DI-3-S9 (smoke F8) — the resume asks the server first ──────────────────

/** The line while the resume reads which originals the server holds. */
export const RECONCILE_CHECKING =
  "Checking with the server what is already there…";
/** The read failed: the resume proceeds on the ledger, as before S9. */
export const RECONCILE_FAILED =
  "could not check the server — sending what the ledger lacks";

/** "370 marked sent here, 3 known missing on the server — sending 160" (by hand, no `toLocale*`). */
export function reconciledLine(
  marked: number,
  knownMissing: number,
  sending: number,
): string {
  return `${grouped(marked)} marked sent here, ${grouped(knownMissing)} known missing on the server — sending ${grouped(sending)}`;
}

function originalsMissing(n: number): string {
  return `${grouped(n)} ${n === 1 ? "original" : "originals"} missing on the server`;
}

/** After a FAILED ingest with `blob_absent`, before / during the automatic pass. */
export function missingAgainLine(n: number): string {
  return `${originalsMissing(n)} — sending ${n === 1 ? "it" : "them"} again`;
}

/** … once the automatic passes are spent (the chip says "upload unfinished"). */
export function missingResumeLine(n: number): string {
  return `${originalsMissing(n)} — resume to send ${n === 1 ? "it" : "them"}`;
}

/**
 * DI-3-S9 — why THIS pass sends what it sends, when there is something to
 * say: the reconcile that ran before it dropped ledger entries, or could not
 * read the server; or the last ingest found originals missing. Null
 * otherwise (a fresh upload, a reconcile that dropped nothing).
 */
export function passCause(u: SetUpload): string | null {
  const r = u.reconcile;
  if (r?.pass === u.pass) {
    if (r.state === "checking") return RECONCILE_CHECKING;
    if (r.state === "failed") return RECONCILE_FAILED;
    if (r.marked > r.kept)
      return reconciledLine(r.marked, r.knownMissing, r.sending);
  }
  if (u.missing && u.missing > 0) return missingAgainLine(u.missing);
  return null;
}

/**
 * The server's present / total: the status' Upload stage (`progress` =
 * `{done: present, total: count}`, DI-3-A; `skipped` for a withholding
 * workflow) first, else the list row's `upload`.
 */
export function serverUploadCounts(
  status: UploadStatusLike | null | undefined,
  listUpload: ServerUploadCounts | null | undefined,
): { present: number; total: number; skipped: boolean } | null {
  const stage = status?.stages?.find((s) => s.key === "upload");
  if (stage?.state === "skipped")
    return { present: 0, total: status?.images.count ?? 0, skipped: true };
  if (stage?.progress)
    return {
      present: stage.progress.done,
      total: stage.progress.total,
      skipped: false,
    };
  if (listUpload)
    return {
      present: listUpload.present,
      total: listUpload.total,
      skipped: !listUpload.required,
    };
  return null;
}

/**
 * "Upload now" may be offered (N2 — the pilot's own act): an "after
 * verification" set that Approve has not released and whose upload has not
 * sent a byte. Never once a byte is up; never on a withholding workflow
 * (`uploadChoice === null`).
 */
export function uploadNowOffered(
  set: UploadSubject | null | undefined,
): boolean {
  const u = set?.upload;
  if (!set || !u || set.uploadChoice !== "after") return false;
  return (
    u.approvedAt === null &&
    u.phase === "queued" &&
    u.bytesSent === 0 &&
    u.filesDone === 0 &&
    u.startedAt === null &&
    Object.keys(u.ledger).length === 0
  );
}

function count(v: number): string {
  return v.toLocaleString("en-US");
}

/** "1,234" by hand — the words a test reads never go through `toLocale*`. */
export function grouped(v: number): string {
  return String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * DI-3-S4-FE — what THIS browser sent of a set's originals: the files its
 * ledger holds (committed blobs, `upload/ledger.ts`), or those the server
 * verified, whichever is more; out of the files of the pass (else `total`,
 * the server's count, else 0). The browser's own knowledge — the server says
 * "present" only once the ingest verified a manifest.
 */
export function ledgerProgress(
  u: SetUpload,
  serverTotal: number | null | undefined,
): { sent: number; total: number } {
  const sent = Math.max(Object.keys(u.ledger).length, u.verified.length);
  // DI-3-S9 — a retry pass's `filesTotal` is its subset (the refused files):
  // after pass 1 the set's total is the server's ("159 of 527", not "of 368")
  const passTotal = u.pass > 1 && serverTotal ? serverTotal : u.filesTotal;
  const total = Math.max(passTotal > 0 ? passTotal : (serverTotal ?? 0), sent);
  return { sent, total };
}

/** Did this browser's upload of the set start (a block went out, or a blob is in the ledger)? */
export function uploadStarted(u: SetUpload): boolean {
  return u.startedAt !== null || Object.keys(u.ledger).length > 0;
}

/** "370 of 527 sent — resume to finish" (DI-3-S4-FE, smoke F4). */
export function unfinishedLine(sent: number, total: number): string {
  return `${grouped(sent)} of ${grouped(total)} sent — resume to finish`;
}

function uploadedOf(present: number, total: number): string {
  return `${count(present)} of ${count(total)} uploaded`;
}

function pct(v: number): string {
  return `${String(Math.round(Math.min(1, Math.max(0, v)) * 100))} %`;
}

/** "uploading 212 of 527 · 3.1 MB/s · 4 min" — rate and time once measured (≥ 1 MiB). */
function uploadingLine(u: SetUpload, nowMs: number): string {
  const head = `uploading ${count(u.filesDone)} of ${count(u.filesTotal)}`;
  const started = u.startedAt ? Date.parse(u.startedAt) : NaN;
  if (!Number.isFinite(started)) return head;
  const rate = uploadRate({
    done: u.sentSinceStart ?? 0,
    elapsedS: Math.max(0, (nowMs - started) / 1000),
  });
  if (!rate.measured) return head;
  const left = remainingS(Math.max(0, u.bytesTotal - u.bytesSent), rate);
  return `${head} · ${formatBytes(rate.perS)}/s · ${formatShort(left)}`;
}

const NOTHING: UploadSummary = {
  text: "",
  kind: null,
  action: null,
  elsewhere: false,
};

function said(text: string, kind: UploadChipKind): UploadSummary {
  return { text, kind, action: null, elsewhere: false };
}

/**
 * DI-3-P7 — ONE sentence per set, the same on the verify card, the open
 * card and the Upload column (its tooltip):
 *
 *   "image upload not required for this workflow"      withholding — never a button
 *   "… — withheld by the approval"                      the wire's `withheldBy: approval`
 *                                                       (DI-3-S10-FE), or — until the next
 *                                                       status read — this tab's approval
 *                                                       (DI-3-S7-FE)
 *   "… — withheld in the field"                         the wire's `withheldBy: pilot`
 *   "527 of 527 uploaded"                               the SERVER's present = total
 *   "upload queued · after set 2"                       the runner's queue
 *   "uploading 212 of 527 · 3.1 MB/s · 4 min"           the runner's pass
 *   "upload stopped at 61 % · retrying"                 a transport stop (edge ak)
 *   "committing" · "verifying 512 of 527"               the runner's pass
 *   "upload failed · 3 files"                           + Retry (this tab only)
 *   "upload after verification — approve to start"     + Upload now (this tab only)
 *   "212 of 527 uploaded — an upload from another browser"
 *                                                       the server's count, no record here
 *   "370 of 527 sent — resume to finish"                DI-3-S4-FE — a record here that is
 *                                                       not live: the ledger's count
 *   "Checking with the server what is already there…"  DI-3-S9 — a resume reads the
 *                                                       server's present names first
 *   "370 marked sent here, 3 known missing on the server — sending 160 · uploading …"
 *                                                       DI-3-S9 — the reconcile dropped
 *                                                       entries (or "could not check the
 *                                                       server — sending what the ledger lacks")
 *   "368 originals missing on the server — sending them again"
 *                                                       DI-3-S9 — a FAILED ingest's
 *                                                       `blob_absent`, the automatic pass
 *   "368 originals missing on the server — resume to send them"
 *                                                       DI-3-S9 — the passes are spent
 *
 * The server is the truth for "uploaded" (the SAS is `cw`: the ledger only
 * says what this browser sent; the ingest says what is present); the runner
 * only for the part in flight.
 */
export function uploadSummary(
  set: UploadSubject | null | undefined,
  status: UploadStatusLike | null | undefined,
  listUpload: ServerUploadCounts | null | undefined,
  workflowUploads: boolean,
  ctx: UploadSummaryContext = {},
): UploadSummary {
  const server = serverUploadCounts(status, listUpload);
  const u = set?.upload ?? null;
  // M5-NODE-01 — the served mode first (story §2.1): under `optional` the
  // SET decides — this browser's record chose an upload (the drop's "now" /
  // "after verification", or the escape hatch), or the server shows bytes
  // of it; `blocked` never uploads; `automatic` / not served → the caller's
  // `workflowUploads` as before
  const mode = set?.imageUpload ?? status?.imageUpload ?? ctx.imageUpload;
  const chosenHere = set?.uploadChoice != null && u !== null;
  const serverShows = server !== null && !server.skipped && server.present > 0;
  const uploads =
    mode === "optional"
      ? chosenHere || serverShows
      : mode === "blocked"
        ? false
        : workflowUploads;
  if (
    !uploads ||
    (server?.skipped === true && !(mode === "optional" && chosenHere)) ||
    (set?.uploadChoice === null && !u && !(mode === "optional" && serverShows))
  )
    return said(
      withheldSentence(
        // the status' once read (it is newer than the list row)
        status?.withheldBy !== undefined ? status.withheldBy : ctx.withheldBy,
        ctx.withheldByApproval,
      ),
      "not_required",
    );

  // the server says every image is present: nothing a runner says overrides it
  if (server && server.total > 0 && server.present >= server.total)
    return said(uploadedOf(server.present, server.total), "done");

  const here = ctx.here === true;
  const live = ctx.live ?? true;
  if (set && u) {
    if (u.phase === "failed") {
      const k = u.failedCount;
      // M5-NODE-01 — the server refused the session (`upload_blocked`,
      // `reason_required`): its words, no Retry (a retry is refused again)
      if (k === 0 && u.error !== null && !u.missing)
        return said(u.error, "failed");
      return {
        text:
          u.missing && u.missing > 0
            ? missingResumeLine(u.missing)
            : `upload failed · ${count(k)} ${k === 1 ? "file" : "files"}`,
        kind: "failed",
        action: "retry",
        elsewhere: !here,
      };
    }
    if (u.phase === "present") {
      const present = Math.max(server?.present ?? 0, u.verified.length);
      const total = Math.max(server?.total ?? u.filesTotal, present);
      return said(uploadedOf(present, total), "done");
    }
    if (uploadNowOffered(set))
      return {
        text: UPLOAD_AFTER_VERIFICATION,
        kind: "queued",
        action: "upload_now",
        elsewhere: !here,
      };
    if (live) {
      const inFlight =
        u.phase === "queued" ||
        u.phase === "uploading" ||
        u.phase === "committing";
      if (inFlight && u.error !== null) {
        const at =
          u.bytesTotal > 0 && u.bytesSent > 0
            ? ` at ${pct(u.bytesSent / u.bytesTotal)}`
            : "";
        return said(`upload stopped${at} · retrying`, "uploading");
      }
      const cause = passCause(u);
      if (cause === RECONCILE_CHECKING)
        return {
          ...said(cause, u.phase === "uploading" ? "uploading" : "queued"),
          cause,
        };
      switch (u.phase) {
        case "queued":
          return cause
            ? { ...said(cause, "queued"), cause }
            : said(`upload ${ctx.queueLabel ?? "queued"}`, "queued");
        case "uploading": {
          const line = uploadingLine(u, ctx.nowMs ?? Date.now());
          return cause
            ? { ...said(`${cause} · ${line}`, "uploading"), cause }
            : said(line, "uploading");
        }
        case "committing":
          return said("committing", "uploading");
        case "ingesting":
          return said(
            `verifying ${count(u.processed)} of ${count(u.total)}`,
            "uploading",
          );
      }
    }
  }

  // DI-3-S4-FE (smoke F4) — a record of this browser that is not live (a
  // reload or a newer drop interrupted it): what the browser's LEDGER knows,
  // not the server's "0 of 527" (the server counts a file present only after
  // the ingest of a manifest that was never completed)
  if (set && u && !live && uploadStarted(u)) {
    const p = ledgerProgress(u, server?.total);
    // the browser knows MORE than the server (else the server's count speaks)
    if (p.total > 0 && p.sent > (server?.present ?? 0))
      return {
        text: unfinishedLine(p.sent, p.total),
        kind: "unfinished",
        action: null,
        elsewhere: false,
      };
  }

  // no runner phase to say (nothing in this browser, or a record that is not
  // live): the server's count alone
  const notStarted = set?.uploadChoice === "after" && !u?.approvedAt;
  if (!server || server.total === 0)
    return notStarted ? said(UPLOAD_AFTER_VERIFICATION, "queued") : NOTHING;
  if (server.present === 0)
    return notStarted
      ? said(UPLOAD_AFTER_VERIFICATION, "queued")
      : said(uploadedOf(0, server.total), "queued");
  const base = uploadedOf(server.present, server.total);
  return said(
    set ? base : `${base} — ${UPLOAD_FROM_ANOTHER_BROWSER}`,
    "uploading",
  );
}

// ── DI-3-R1-FE-pilot — "Images required" (DI-3-R1 4.7 + Christian's addendum) ──

export interface ImagesRequiredInput {
  /** The set's workflow uploads the originals (`upload.required`, the catalogue). */
  required: boolean;
  /** The server's present / total (`serverUploadCounts`). */
  counts: { present: number; total: number; skipped: boolean } | null;
  /** The set is in the intake AGAIN: sent back (`sentBack`), or reactivated here. */
  backInIntake: boolean;
  /** This browser's runner holds the set's files (its index is loaded here). */
  filesHere: boolean;
}

/**
 * The red line's rule (story §1.4): an upload set, in the intake again, whose
 * originals are not all on the server (`present < total`) and whose files no
 * runner of this browser holds. Never for a partner set (nothing required),
 * never once every image is present, never while the files are bound here.
 */
export function imagesRequired(i: ImagesRequiredInput): boolean {
  if (!i.required || !i.backInIntake || i.filesHere) return false;
  const c = i.counts;
  if (!c || c.skipped || c.total === 0) return false;
  return c.present < c.total;
}

// ── DI-3-S64 — reachable, not uploaded (POOL-1 option A) ──────────────────

/**
 * DI-3-S64 (supersedes the DI-3-R1 4.7 addendum, Christian 2026-10-26) —
 * the images rule as three answers:
 *
 *   ok       not required, not back in the intake, all present on the
 *            server, a runner of this browser holds the files, OR the set's
 *            chosen folder holds ALL of its originals (counted by payload
 *            hash, `upload/fromFolder.ts:prepareFolder`);
 *   partial  a chosen folder holds SOME (0 < found < total) — a question,
 *            never a blocker;
 *   missing  no folder known (or one not counted yet), or it holds none —
 *            the red blocker.
 */
export type ImagesState = "ok" | "partial" | "missing";

export function imagesState(
  i: ImagesRequiredInput & {
    /** The folder chosen for the set, as counted (`found` null: not counted). */
    folder?: { found: number | null; total: number | null } | null;
  },
): ImagesState {
  if (!imagesRequired(i)) return "ok";
  const found = i.folder?.found ?? null;
  const total = i.folder?.total ?? null;
  if (found === null || total === null || found <= 0) return "missing";
  return total > 0 && found >= total ? "ok" : "partial";
}

/** DI-3-S64 §2.2 — the upload sentence of a set whose originals are reachable here. */
export function originalsReadyLine(total: number, folder: string): string {
  return `${total.toLocaleString("en-US")} ${total === 1 ? "original" : "originals"} ready in ${folder} — they upload after you approve`;
}
